import { createContext, PropsWithChildren, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'

export type ModalOptions = {
  /** false blocks ESC/outside-press dismissal; the modal must close itself. */
  dismissable?: boolean
  onClose?: () => void
}

type ModalEntry = {
  content: ReactNode
  options: ModalOptions
}

interface ModalContextType {
  modal: ModalEntry | null
  isOpen: boolean
  /** Opens a modal; an already open one is replaced (its onClose fires). */
  openModal: (content: ReactNode, options?: ModalOptions) => void
  closeModal: () => void
}

const ModalContext = createContext<ModalContextType | null>(null)

export const ModalProvider = ({ children }: PropsWithChildren) => {
  const [modal, setModal] = useState<ModalEntry | null>(null)
  // Mirror of `modal` so open/close callbacks stay referentially stable.
  const modalRef = useRef<ModalEntry | null>(null)

  const openModal = useCallback((content: ReactNode, options: ModalOptions = {}) => {
    modalRef.current?.options.onClose?.()
    modalRef.current = { content, options }
    setModal(modalRef.current)
  }, [])

  const closeModal = useCallback(() => {
    if (!modalRef.current) return
    modalRef.current.options.onClose?.()
    modalRef.current = null
    setModal(null)
  }, [])

  const contextValue = useMemo<ModalContextType>(
    () => ({ modal, isOpen: !!modal, openModal, closeModal }),
    [modal, openModal, closeModal],
  )

  return <ModalContext.Provider value={contextValue}>{children}</ModalContext.Provider>
}

export const useModal = () => {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error("useModal deve essere usato all'interno di un ModalProvider")
  }
  return context
}

type ModalOutletProps = {
  /** 'sheet' = widget bottom sheet (default), 'dialog' = centered fullscreen card for the chat page. */
  variant?: 'sheet' | 'dialog'
}

export const ModalOutlet = ({ variant = 'sheet' }: ModalOutletProps) => {
  const { modal, isOpen, closeModal } = useModal()

  // Base UI drives ESC + outside-press through onOpenChange; the header's X button
  // calls closeModal directly. dismissable:false blocks only ESC/outside-press.
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) return
      if (modal?.options.dismissable === false) return
      closeModal()
    },
    [modal, closeModal],
  )

  // Sheet popups portal into a host node the outlet renders itself: the outlet lives inside
  // the widget box, so backdrop/sheet stay clipped by the box and die with it — no global ids.
  const sheetHostRef = useRef<HTMLDivElement>(null)

  if (variant === 'dialog') {
    return (
      <DialogPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Backdrop className="animate-fade-in-medium fixed inset-0 z-998 bg-black/50 backdrop-blur-xs" />
          <DialogPrimitive.Popup className="animate-scale-in fixed top-1/2 left-1/2 z-998 max-h-[90vh] w-full max-w-md -translate-x-1/2 -translate-y-1/2 overflow-auto rounded-2xl border border-border bg-background shadow-2xl outline-none">
            {modal?.content}
          </DialogPrimitive.Popup>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    )
  }

  return (
    <>
      <div ref={sheetHostRef} style={{ display: 'contents' }} />
      <DialogPrimitive.Root open={isOpen} onOpenChange={handleOpenChange} modal="trap-focus">
        <DialogPrimitive.Portal container={sheetHostRef}>
          <DialogPrimitive.Backdrop className="animate-fade-in absolute inset-0 z-998 overflow-hidden rounded-2xl bg-black/50" />
          <DialogPrimitive.Popup className="animate-slide-up-modal absolute inset-x-0 bottom-0 z-998 max-h-[90%] overflow-y-auto rounded-t-2xl border-t border-border bg-background shadow-lg outline-none">
            {modal?.content}
          </DialogPrimitive.Popup>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </>
  )
}
